/* Mensajes del proyecto (F19): el hilo sobre el trabajo, pegado al trabajo.

   Si la conversación vive en correo, solo la tiene quien estuvo copiado y
   desaparece en cuanto alguien entra o sale del equipo. Aquí la hereda quien
   llegue. Se menciona con @nombre y se puede señalar un job concreto. Hereda
   los permisos del proyecto: quien no tiene el cliente no ve nada, y una
   mención a alguien sin acceso no procede — el servidor la rechaza con nombre
   y el texto se queda en la caja para corregirlo. */
import { api, ApiError, type MensajeProyecto as Mensaje, type Mencion, type Mencionable } from "../api";
import { estado } from "../estado";
import { esc, Fechas, avatar, primerNombre, toast } from "../ui";
import type { Vista } from "./tipos";
import { listaEncargosHtml, montarEncargos } from "./encargos";

const plano = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/** Ids mencionados en un texto: @Nombre Apellido o @Nombre, contra la lista de mencionables. */
export function mencionesEn(texto: string, gente: Mencionable[]): string[] {
  const t = plano(texto); const ids: string[] = [];
  for (const g of gente) {
    const completo = "@" + plano(g.nombre), corto = "@" + plano(primerNombre(g.nombre));
    if (t.includes(completo) || new RegExp(corto.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "(?![a-z0-9])").test(t)) ids.push(g.id);
  }
  return ids;
}

/** El texto con las menciones resaltadas. Solo se resalta a quien de verdad quedó mencionado. */
function textoHtml(m: Mensaje) {
  let h = esc(m.texto);
  for (const p of m.menciones) {
    for (const forma of [p.nombre, primerNombre(p.nombre)]) {
      const re = new RegExp("@" + esc(forma).replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "(?![\\p{L}\\p{N}])", "giu");
      h = h.replace(re, (x) => '<span class="mencion">' + x + "</span>");
    }
  }
  return h.replace(/\n/g, "<br>");
}

export function mensajeHtml(m: Mensaje, o?: { resaltar?: boolean; enlaceJob?: boolean }) {
  const yo = estado.usuario()?.id === m.autor.id;
  return '<div class="msg' + (o?.resaltar ? " resaltado" : "") + '" id="msg-' + esc(m.id) + '">' + avatar(m.autor) +
    '<div class="cuerpo"><div class="quien"><b>' + esc(yo ? "Tú" : m.autor.nombre) + "</b>" + (m.autor.tipo === "ai" ? ' <span class="tag" style="font-size:10px;padding:1px 5px;background:var(--accentBg);color:var(--accent)">AI</span>' : "") +
      '<span class="cuando">' + esc(Fechas.relativa(m.creadoEn)) + "</span>" +
      (m.job ? '<span class="sobre"' + (o?.enlaceJob !== false && m.jobId ? ' data-go="#/jobs/' + esc(m.jobId) + '" style="cursor:pointer"' : "") + ">sobre " + esc(m.job) + "</span>" : "") + "</div>" +
      '<div class="texto">' + textoHtml(m) + "</div></div></div>";
}

export function hiloHtml(mensajes: Mensaje[], o: { vacio?: string; resaltarId?: string | undefined }) {
  if (!mensajes.length) return '<div class="vacio">' + esc(o.vacio || "Nadie ha escrito todavía. Lo que se diga aquí lo hereda quien entre al proyecto.") + "</div>";
  return '<div class="hilo">' + mensajes.map((m) => mensajeHtml(m, { resaltar: m.id === o.resaltarId })).join("") + "</div>";
}

export function composerHtml(o: { cliente: string; jobs: { id: string; nombre: string }[]; mencionables: Mencionable[]; cerrado: boolean; jobId?: string }) {
  if (o.cerrado) return '<div class="msg-composer cerrado">El proyecto está cerrado: el hilo se consulta, no se escribe.</div>';
  const yo = estado.usuario()?.id;
  const gente = o.mencionables.filter((g) => g.id !== yo);
  return '<div class="msg-composer">' +
    '<textarea id="ms-texto" rows="2" placeholder="Escribe al equipo. @nombre para mencionar a alguien."></textarea>' +
    '<div class="fila-c">' +
      '<select id="ms-job"><option value="">Sobre el proyecto en general</option>' + o.jobs.map((j) => '<option value="' + esc(j.id) + '"' + (j.id === o.jobId ? " selected" : "") + ">sobre " + esc(j.nombre) + "</option>").join("") + "</select>" +
      '<select id="ms-mencionar"><option value="">Mencionar a…</option>' + gente.map((g) => '<option value="' + esc(g.id) + '" data-nombre="' + esc(g.nombre) + '">' + esc(g.nombre) + " · " + esc(g.rol) + "</option>").join("") + "</select>" +
      '<button class="btn primario chico" id="ms-publicar">Publicar</button></div>' +
    '<div class="nota">Hereda los permisos del proyecto: quien no tiene ' + esc(o.cliente) + " no ve este hilo. Mencionar a alguien sin acceso no procede.</div></div>";
}

/** Enlaza el composer: menciones por @, job señalado, publicación y repintado del hilo. */
export function montarComposer(root: HTMLElement, proyectoId: string, mencionables: Mencionable[], alPublicar: (m: Mensaje) => void) {
  const ta = root.querySelector<HTMLTextAreaElement>("#ms-texto"); if (!ta) return;
  const sel = root.querySelector<HTMLSelectElement>("#ms-mencionar")!; const job = root.querySelector<HTMLSelectElement>("#ms-job")!; const btn = root.querySelector<HTMLButtonElement>("#ms-publicar")!;
  sel.onchange = () => {
    const op = sel.selectedOptions[0]; if (!op || !op.value) return;
    const nombre = "@" + primerNombre(op.dataset.nombre || "") + " ";
    const i = ta.selectionStart ?? ta.value.length;
    ta.value = ta.value.slice(0, i) + (i && !/\s$/.test(ta.value.slice(0, i)) ? " " : "") + nombre + ta.value.slice(i);
    sel.value = ""; ta.focus();
  };
  const publicar = async () => {
    const texto = ta.value.trim(); if (!texto) { toast("Escribe algo primero."); return; }
    btn.disabled = true;
    try {
      const m = await api.publicarMensaje(proyectoId, { texto, jobId: job.value || null, menciones: mencionesEn(texto, mencionables) });
      ta.value = ""; alPublicar(m);
    } catch (e) {
      const err = e as ApiError;
      toast("<b>" + esc(err.codigo === "mencion_sin_acceso" ? "La mención no procede." : err.codigo === "proyecto_cerrado" ? "Proyecto cerrado." : "No se publicó.") + "</b> " + esc(err.mensaje || err.message));
    } finally { btn.disabled = false; }
  };
  btn.onclick = () => void publicar();
  ta.onkeydown = (ev) => { if (ev.key === "Enter" && (ev.metaKey || ev.ctrlKey)) { ev.preventDefault(); void publicar(); } };
}

// ------------------------------------------------------------ #/menciones: la bandeja
export async function menciones(): Promise<Vista> {
  const [lista, encargos] = await Promise.all([api.menciones(), api.encargos().catch(() => [])]);
  const sinLeer = lista.filter((m) => !m.leidaEn);
  // Lo que el equipo AI pregunta y lo que está produciendo para ti (F23): es
  // "algo dirigido a mí", igual que una mención, y vive en la misma bandeja.
  const preguntas = encargos.filter((e) => e.estado === "pregunta");
  const fila = (m: Mencion) => '<div class="fila" data-mencion="' + esc(m.id) + '" data-destino="#/proyectos/' + esc(m.proyectoId) + "?mensaje=" + esc(m.mensajeId) + '"><span class="dot" style="background:' + (m.leidaEn ? "var(--border3)" : "var(--accent)") + '"></span>' +
    '<div style="flex-grow:1;min-width:0"><div class="t">' + esc(m.autor.nombre) + " te mencionó en " + esc(m.proyecto) + (m.job ? " · sobre " + esc(m.job) : "") + '</div><div class="m" style="white-space:normal">' + esc(m.texto.length > 160 ? m.texto.slice(0, 157) + "…" : m.texto) + "</div></div>" +
    '<span class="dot" style="background:' + esc(m.cliente.color) + '" title="' + esc(m.cliente.nombre) + '"></span><span style="font-size:11.5px;color:var(--text3);white-space:nowrap">' + esc(Fechas.relativa(m.creadoEn)) + "</span></div>";
  return {
    titulo: "Menciones", miga: ["Menciones"],
    sub: (preguntas.length ? preguntas.length + " pregunta" + (preguntas.length > 1 ? "s" : "") + " del equipo AI · " : "") + (sinLeer.length ? sinLeer.length + " sin leer · alguien te nombró en el hilo de un proyecto" : "nadie te ha mencionado sin que lo leas"),
    acciones: sinLeer.length ? '<button class="btn" id="mn-todas">Marcar todas como leídas</button>' : "",
    html:
      (encargos.length ? '<div class="card"' + (preguntas.length ? ' style="border-color:var(--critBorder)"' : "") + '><div class="cab"' + (preguntas.length ? ' style="background:var(--critTint)"' : "") + '><div class="t"' + (preguntas.length ? ' style="color:var(--crit)"' : "") + '>El equipo AI' + (preguntas.length ? " te pregunta · " + preguntas.length : " · lo que pediste") + '</div><span style="font-size:12px;color:var(--text3)">produce en segundo plano; si pregunta, se le contesta aquí y sigue solo</span></div><div style="padding:12px 16px">' + listaEncargosHtml(encargos, { vacio: "" }) + "</div></div>" : "") +
      '<div class="card">' + (lista.length ? lista.map(fila).join("") : '<div class="vacio">Cuando alguien escriba @' + esc(primerNombre(estado.usuario()?.nombre || "")) + " en el hilo de un proyecto que tienes, aparece aquí.</div>") + "</div>" +
      '<div style="font-size:11.5px;color:var(--text3);margin-top:10px">Solo ves menciones de proyectos cuyos clientes tienes. Si el acceso se retira, la mención desaparece con él.</div>',
    despues(root) {
      root.querySelectorAll<HTMLElement>("[data-mencion]").forEach((f) => f.onclick = async () => {
        try { await api.leerMencion(f.dataset.mencion!); } catch { /* se marca al abrir el hilo */ }
        location.hash = f.dataset.destino!;
      });
      const t = root.querySelector<HTMLButtonElement>("#mn-todas"); if (t) t.onclick = async () => { await api.leerTodasLasMenciones(); toast("Todas leídas."); location.reload(); };
      montarEncargos(root, () => location.reload());
    },
  };
}
